import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { API, useAuth } from '../App';
import Navbar from '../components/shared/Navbar';
import DashboardSidebar from '../components/shared/DashboardSidebar';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import {
  ArrowLeft, Package, MapPin, Calendar, DollarSign, Truck, FileText,
  User, Building2, Phone, Mail, MessageSquare, CheckCircle, Clock,
  XCircle, Loader2, Send, Download, CreditCard, Circle, Eye, X
} from 'lucide-react';
import LiveChat from '../components/shared/LiveChat';
import UploadedDocuments from '../components/shared/UploadedDocuments';
import OrderDocumentChecklist from '../components/shared/OrderDocumentChecklist';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../components/ui/dialog';

const OrderDetailPage = () => {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [newStatus, setNewStatus] = useState('');
  const [message, setMessage] = useState('');
  const [sendingMessage, setSendingMessage] = useState(false);
  const [showLiveChat, setShowLiveChat] = useState(false);
  const [showDocuments, setShowDocuments] = useState(false);
  const [docsVersion, setDocsVersion] = useState(0);
  const [showPayment, setShowPayment] = useState(false);
  const [downloadingInvoice, setDownloadingInvoice] = useState(false);
  const [processingPayment, setProcessingPayment] = useState(false);

  // Determine if current user is buyer or seller
  const isBuyer = order?.buyer_id === user?.user_id;
  const isSeller = order?.seller_id === user?.user_id;

  const fetchOrderDetails = useCallback(async () => {
    try {
      const token = localStorage.getItem('atff_token');
      const response = await axios.get(`${API}/orders/${orderId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setOrder(response.data);
      setNewStatus(response.data.status);
    } catch (error) {
      toast.error('Failed to load order details');
    } finally {
      setLoading(false);
    }
  }, [orderId]);

  useEffect(() => {
    fetchOrderDetails();
  }, [fetchOrderDetails]);

  const handleStatusUpdate = async () => {
    if (!newStatus || newStatus === order.status) return;
    
    setUpdating(true);
    try {
      const token = localStorage.getItem('atff_token');
      await axios.put(`${API}/orders/${orderId}/status`, 
        { status: newStatus },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Order status updated');
      fetchOrderDetails();
    } catch (error) {
      console.error('Error updating status:', error);
      toast.error(error.response?.data?.detail || 'Failed to update status');
    } finally {
      setUpdating(false);
    }
  };

  const handleSendMessage = async () => {
    if (!message.trim()) return;
    
    setSendingMessage(true);
    try {
      const token = localStorage.getItem('atff_token');
      await axios.post(`${API}/orders/${orderId}/messages`, 
        { message: message.trim() },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Message sent');
      setMessage('');
      fetchOrderDetails();
    } catch (error) {
      console.error('Error sending message:', error);
      toast.error('Failed to send message');
    } finally {
      setSendingMessage(false);
    }
  };

  // Generate and download invoice as PDF
  const handleDownloadInvoice = async () => {
    setDownloadingInvoice(true);
    try {
      // Create invoice content
      const invoiceContent = generateInvoiceHTML();
      
      // Create blob and trigger download
      const blob = new Blob([invoiceContent], { type: 'text/html' });
      const url = URL.createObjectURL(blob);
      
      // Open in new window for printing/saving as PDF
      const printWindow = window.open(url, '_blank');
      if (printWindow) {
        printWindow.onload = () => {
          printWindow.print();
        };
      }
      
      toast.success('Invoice generated! Use your browser\'s print dialog to save as PDF.');
    } catch (error) {
      toast.error('Failed to generate invoice');
    } finally {
      setDownloadingInvoice(false);
    }
  };

  const generateInvoiceHTML = () => {
    const invoiceDate = new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
    const dueDate = new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
    
    return `
<!DOCTYPE html>
<html>
<head>
  <title>Invoice - ${order.order_id}</title>
  <style>
    * { margin: 0; padding: 0; box-sizing: border-box; }
    body { font-family: 'Segoe UI', Arial, sans-serif; padding: 40px; color: #333; }
    .header { display: flex; justify-content: space-between; margin-bottom: 40px; }
    .logo { font-size: 28px; font-weight: bold; color: #1B5E20; }
    .logo span { color: #FFB300; }
    .invoice-title { text-align: right; }
    .invoice-title h1 { font-size: 32px; color: #1B5E20; }
    .invoice-title p { color: #666; margin-top: 5px; }
    .parties { display: flex; justify-content: space-between; margin-bottom: 30px; }
    .party { width: 45%; }
    .party h3 { color: #1B5E20; font-size: 14px; margin-bottom: 10px; text-transform: uppercase; }
    .party p { margin: 3px 0; font-size: 14px; }
    .order-info { background: #f5f5f5; padding: 15px; border-radius: 8px; margin-bottom: 30px; }
    .order-info-row { display: flex; justify-content: space-between; }
    .order-info-item { text-align: center; }
    .order-info-item label { font-size: 12px; color: #666; }
    .order-info-item p { font-weight: bold; margin-top: 5px; }
    table { width: 100%; border-collapse: collapse; margin-bottom: 30px; }
    th { background: #1B5E20; color: white; padding: 12px; text-align: left; font-size: 14px; }
    td { padding: 12px; border-bottom: 1px solid #eee; font-size: 14px; }
    .totals { width: 300px; margin-left: auto; }
    .totals tr td { padding: 8px 12px; }
    .totals tr:last-child { background: #1B5E20; color: white; font-weight: bold; }
    .footer { margin-top: 40px; text-align: center; color: #666; font-size: 12px; }
    .afcfta-badge { display: inline-block; background: #E8F5E9; color: #1B5E20; padding: 5px 15px; border-radius: 20px; font-size: 12px; font-weight: bold; margin-top: 10px; }
    @media print { body { padding: 20px; } }
  </style>
</head>
<body>
  <div class="header">
    <div class="logo">ATFF<span>trade</span>.africa</div>
    <div class="invoice-title">
      <h1>INVOICE</h1>
      <p>${order.order_id}</p>
    </div>
  </div>
  
  <div class="parties">
    <div class="party">
      <h3>From (Seller)</h3>
      <p><strong>${order.seller_name || 'N/A'}</strong></p>
      <p>${order.seller_country || 'Nigeria'}</p>
      <p>${order.contact_email || ''}</p>
      <span class="afcfta-badge">✓ AfCFTA Verified</span>
    </div>
    <div class="party">
      <h3>To (Buyer)</h3>
      <p><strong>${order.buyer_name || 'N/A'}</strong></p>
      <p>${order.shipping_address || 'N/A'}</p>
      <p>${order.delivery_country || 'N/A'}</p>
    </div>
  </div>
  
  <div class="order-info">
    <div class="order-info-row">
      <div class="order-info-item">
        <label>Invoice Date</label>
        <p>${invoiceDate}</p>
      </div>
      <div class="order-info-item">
        <label>Due Date</label>
        <p>${dueDate}</p>
      </div>
      <div class="order-info-item">
        <label>Order Status</label>
        <p>${order.status?.toUpperCase()}</p>
      </div>
      <div class="order-info-item">
        <label>Payment Status</label>
        <p>${(order.payment_status || 'UNPAID').toUpperCase()}</p>
      </div>
    </div>
  </div>
  
  <table>
    <thead>
      <tr>
        <th>Product</th>
        <th>HS Code</th>
        <th>Quantity</th>
        <th>Unit Price</th>
        <th>Total</th>
      </tr>
    </thead>
    <tbody>
      <tr>
        <td>${order.product_name}</td>
        <td>${order.hs_code || 'N/A'}</td>
        <td>${order.quantity} units</td>
        <td>${formatCurrency(order.unit_price, order.currency)}</td>
        <td>${formatCurrency(order.quantity * order.unit_price, order.currency)}</td>
      </tr>
    </tbody>
  </table>
  
  <table class="totals">
    <tr>
      <td>Subtotal</td>
      <td>${formatCurrency(order.quantity * order.unit_price, order.currency)}</td>
    </tr>
    <tr>
      <td>AfCFTA Tariff Discount</td>
      <td>- ${formatCurrency(0, order.currency)}</td>
    </tr>
    <tr>
      <td>Shipping</td>
      <td>${order.shipping_cost ? formatCurrency(order.shipping_cost, order.currency) : 'TBD'}</td>
    </tr>
    <tr>
      <td>Total Amount</td>
      <td>${formatCurrency((order.quantity * order.unit_price) + (order.shipping_cost || 0), order.currency)}</td>
    </tr>
  </table>
  
  <div class="footer">
    <p>This invoice was generated by ATFFtrade.africa - Africa's Digital Trade Infrastructure</p>
    <p>For questions, contact support@atfftrade.africa</p>
    <p style="margin-top: 10px;">AfCFTA Compliant Trade Document</p>
  </div>
</body>
</html>`;
  };

  // Handle payment initiation
  const handleMakePayment = async () => {
    setProcessingPayment(true);
    try {
      const token = localStorage.getItem('atff_token');
      
      // Create payment session via backend
      const response = await axios.post(`${API}/payments/create-session`, {
        order_id: order.order_id,
        amount: order.quantity * order.unit_price,
        currency: order.currency || 'USD'
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.checkout_url) {
        // Redirect to Stripe checkout
        window.location.href = response.data.checkout_url;
      } else if (response.data.payment_id) {
        // PAPSS simulation - show success
        toast.success('Payment initiated via PAPSS! Reference: ' + response.data.payment_id);
        setShowPayment(false);
        fetchOrderDetails();
      }
    } catch (error) {
      // Fallback: Show payment modal with simulation
      toast.info('Payment gateway temporarily unavailable. Showing simulation mode.');
      setShowPayment(true);
    } finally {
      setProcessingPayment(false);
    }
  };

  // Simulate payment completion (for demo)
  const handleSimulatePayment = async () => {
    setProcessingPayment(true);
    try {
      const token = localStorage.getItem('atff_token');
      await axios.put(`${API}/orders/${orderId}/payment`, {
        payment_status: 'paid',
        payment_method: 'PAPSS',
        payment_reference: `PAPSS-${Date.now()}`
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Payment successful! Order marked as paid.');
      setShowPayment(false);
      fetchOrderDetails();
    } catch (error) {
      toast.error('Payment simulation failed. Please try again.');
    } finally {
      setProcessingPayment(false);
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: { bg: 'bg-yellow-100', text: 'text-yellow-700', icon: Clock },
      confirmed: { bg: 'bg-blue-100', text: 'text-blue-700', icon: CheckCircle },
      processing: { bg: 'bg-purple-100', text: 'text-purple-700', icon: Package },
      shipped: { bg: 'bg-orange-100', text: 'text-orange-700', icon: Truck },
      delivered: { bg: 'bg-green-100', text: 'text-green-700', icon: CheckCircle },
      completed: { bg: 'bg-green-100', text: 'text-green-700', icon: CheckCircle },
      cancelled: { bg: 'bg-red-100', text: 'text-red-700', icon: XCircle },
    };
    const config = statusConfig[status] || statusConfig.pending;
    const Icon = config.icon;
    
    return (
      <Badge className={`${config.bg} ${config.text} px-3 py-1`}>
        <Icon className="w-4 h-4 mr-1" />
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </Badge>
    );
  };

  const formatCurrency = (amount, currency = 'USD') => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency
    }).format(amount);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navbar />
        <div className="flex">
          <DashboardSidebar />
          <main className="flex-1 p-6 flex items-center justify-center">
            <Loader2 className="w-8 h-8 animate-spin text-continental" />
          </main>
        </div>
      </div>
    );
  }

  if (!order) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navbar />
        <div className="flex">
          <DashboardSidebar />
          <main className="flex-1 p-6">
            <div className="text-center py-12">
              <Package className="w-16 h-16 mx-auto text-gray-300 mb-4" />
              <h2 className="text-xl font-semibold text-gray-600">Order not found</h2>
              <Button onClick={() => navigate(-1)} className="mt-4">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Go Back
              </Button>
            </div>
          </main>
        </div>
      </div>
    );
  }

  const totalAmount = (order.quantity || 0) * (order.unit_price || 0);

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      <div className="flex">
        <DashboardSidebar />
        
        <main className="flex-1 p-6">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-4">
              <Button variant="ghost" onClick={() => navigate(-1)}>
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back
              </Button>
              <div>
                <h1 className="text-2xl font-bold text-charcoal">Order #{order.order_id.slice(-8).toUpperCase()}</h1>
                <p className="text-gray-500">
                  {isBuyer ? 'Your Purchase' : 'Customer Order'} • Created {formatDate(order.created_at)}
                </p>
              </div>
            </div>
            {getStatusBadge(order.status)}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Main Order Info */}
            <div className="lg:col-span-2 space-y-6">
              {/* Product Details */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Package className="w-5 h-5 text-continental" />
                    Product Details
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex items-start gap-4">
                    <div className="w-24 h-24 bg-gray-100 rounded-lg flex items-center justify-center">
                      <Package className="w-10 h-10 text-gray-400" />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-lg font-semibold text-charcoal">{order.product_name}</h3>
                      <p className="text-gray-500 text-sm mb-3">Product ID: {order.product_id}</p>
                      
                      <div className="grid grid-cols-2 gap-4 text-sm">
                        <div>
                          <p className="text-gray-500">Quantity</p>
                          <p className="font-semibold">{order.quantity} units</p>
                        </div>
                        <div>
                          <p className="text-gray-500">Unit Price</p>
                          <p className="font-semibold">{formatCurrency(order.unit_price, order.currency)}</p>
                        </div>
                        <div>
                          <p className="text-gray-500">Total Amount</p>
                          <p className="font-semibold text-continental text-lg">{formatCurrency(totalAmount, order.currency)}</p>
                        </div>
                        <div>
                          <p className="text-gray-500">Payment Status</p>
                          <Badge variant="outline" className={order.payment_status === 'paid' ? 'text-green-600' : 'text-yellow-600'}>
                            {order.payment_status || 'Unpaid'}
                          </Badge>
                        </div>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Shipping Details */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Truck className="w-5 h-5 text-orange-500" />
                    Shipping Information
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="flex items-start gap-3">
                      <MapPin className="w-5 h-5 text-gray-400 mt-0.5" />
                      <div>
                        <p className="text-sm text-gray-500">Delivery Address</p>
                        <p className="font-medium">{order.shipping_address || 'Not specified'}</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <Package className="w-5 h-5 text-gray-400 mt-0.5" />
                      <div>
                        <p className="text-sm text-gray-500">Delivery Country</p>
                        <p className="font-medium">{order.delivery_country || 'Not specified'}</p>
                      </div>
                    </div>
                    {order.tracking_number && (
                      <div className="flex items-start gap-3 md:col-span-2">
                        <Truck className="w-5 h-5 text-gray-400 mt-0.5" />
                        <div>
                          <p className="text-sm text-gray-500">Tracking Number</p>
                          <p className="font-medium font-mono">{order.tracking_number}</p>
                        </div>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Order Notes */}
              {order.notes && (
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <FileText className="w-5 h-5 text-blue-500" />
                      Order Notes
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-gray-600">{order.notes}</p>
                  </CardContent>
                </Card>
              )}

              {/* Status Update (Seller Only) */}
              {isSeller && (
                <Card>
                  <CardHeader>
                    <CardTitle>Update Order Status</CardTitle>
                    <CardDescription>Change the order status to update the buyer</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="flex items-center gap-4">
                      <Select value={newStatus} onValueChange={setNewStatus}>
                        <SelectTrigger className="w-48">
                          <SelectValue placeholder="Select status" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="pending">Pending</SelectItem>
                          <SelectItem value="confirmed">Confirmed</SelectItem>
                          <SelectItem value="processing">Processing</SelectItem>
                          <SelectItem value="shipped">Shipped</SelectItem>
                          <SelectItem value="delivered">Delivered</SelectItem>
                          <SelectItem value="completed">Completed</SelectItem>
                          <SelectItem value="cancelled">Cancelled</SelectItem>
                        </SelectContent>
                      </Select>
                      <Button 
                        onClick={handleStatusUpdate} 
                        disabled={updating || newStatus === order.status}
                        className="bg-continental hover:bg-continental/90"
                      >
                        {updating ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                        Update Status
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>

            {/* Sidebar - Contact & Actions */}
            <div className="space-y-6">
              {/* Contact Card */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <User className="w-5 h-5 text-continental" />
                    {isBuyer ? 'Seller Information' : 'Buyer Information'}
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-continental/10 rounded-full flex items-center justify-center">
                      <Building2 className="w-6 h-6 text-continental" />
                    </div>
                    <div>
                      <p className="font-semibold">{isBuyer ? order.seller_name : order.buyer_name}</p>
                      <p className="text-sm text-gray-500">{isBuyer ? 'Seller' : 'Buyer'}</p>
                    </div>
                  </div>
                  
                  {order.contact_email && (
                    <div className="flex items-center gap-2 text-sm">
                      <Mail className="w-4 h-4 text-gray-400" />
                      <a href={`mailto:${order.contact_email}`} className="text-continental hover:underline">
                        {order.contact_email}
                      </a>
                    </div>
                  )}
                  
                  {order.contact_phone && (
                    <div className="flex items-center gap-2 text-sm">
                      <Phone className="w-4 h-4 text-gray-400" />
                      <span>{order.contact_phone}</span>
                    </div>
                  )}

                  {order.inquiry_id ? (
                    <Button 
                      variant="outline" 
                      className="w-full" 
                      onClick={() => setShowLiveChat(true)}
                      data-testid="open-order-chat-btn"
                    >
                      <Circle className="w-3 h-3 mr-2 fill-green-500 text-green-500" />
                      Live Chat
                    </Button>
                  ) : order.contact_email && (
                    <Button 
                      variant="outline" 
                      className="w-full"
                      onClick={() => window.location.href = `mailto:${order.contact_email}?subject=Order%20${order.order_id}`}
                      data-testid="email-contact-btn"
                    >
                      <Mail className="w-4 h-4 mr-2" />
                      Send Email
                    </Button>
                  )}
                </CardContent>
              </Card>

              {/* Quick Actions */}
              <Card>
                <CardHeader>
                  <CardTitle>Quick Actions</CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  <Button 
                    variant="outline" 
                    className="w-full justify-start"
                    onClick={handleDownloadInvoice}
                    disabled={downloadingInvoice}
                    data-testid="download-invoice-btn"
                  >
                    {downloadingInvoice ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Download className="w-4 h-4 mr-2" />
                    )}
                    Download Invoice
                  </Button>
                  <Button 
                    variant="outline" 
                    className="w-full justify-start"
                    onClick={() => setShowDocuments(true)}
                    data-testid="view-documents-btn"
                  >
                    <Eye className="w-4 h-4 mr-2" />
                    View Documents
                  </Button>
                  {isBuyer && order.payment_status !== 'paid' && (
                    <Button 
                      className="w-full bg-continental hover:bg-continental/90"
                      onClick={handleMakePayment}
                      disabled={processingPayment}
                      data-testid="make-payment-btn"
                    >
                      {processingPayment ? (
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <CreditCard className="w-4 h-4 mr-2" />
                      )}
                      Make Payment
                    </Button>
                  )}
                </CardContent>
              </Card>

              {/* Order Timeline */}
              <Card>
                <CardHeader>
                  <CardTitle>Order Timeline</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <TimelineItem 
                      icon={CheckCircle}
                      title="Order Created"
                      date={formatDate(order.created_at)}
                      active
                    />
                    {order.status !== 'pending' && (
                      <TimelineItem 
                        icon={CheckCircle}
                        title="Order Confirmed"
                        date={order.confirmed_at ? formatDate(order.confirmed_at) : '-'}
                        active={['confirmed', 'processing', 'shipped', 'delivered', 'completed'].includes(order.status)}
                      />
                    )}
                    {['shipped', 'delivered', 'completed'].includes(order.status) && (
                      <TimelineItem 
                        icon={Truck}
                        title="Shipped"
                        date={order.shipped_at ? formatDate(order.shipped_at) : '-'}
                        active
                      />
                    )}
                    {['delivered', 'completed'].includes(order.status) && (
                      <TimelineItem 
                        icon={CheckCircle}
                        title="Delivered"
                        date={order.delivered_at ? formatDate(order.delivered_at) : '-'}
                        active
                      />
                    )}
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>

          {/* Live Chat Widget */}
          {order.inquiry_id && (
            <LiveChat
              inquiryId={order.inquiry_id}
              currentUserId={user?.user_id}
              otherPartyName={isBuyer ? order.seller_name : order.buyer_name}
              otherPartyId={isBuyer ? order.seller_id : order.buyer_id}
              productName={order.product_name}
              isOpen={showLiveChat}
              onClose={() => setShowLiveChat(false)}
            />
          )}

          {/* Documents Modal */}
          <Dialog open={showDocuments} onOpenChange={setShowDocuments}>
            <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <FileText className="w-5 h-5 text-continental" />
                  Order Documents
                </DialogTitle>
                <DialogDescription>
                  Documents associated with order {order.order_id}
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4 mt-4">
                <div className="p-4 border rounded-lg bg-gray-50">
                  <OrderDocumentChecklist orderId={order.order_id} refreshKey={docsVersion} onChange={() => setDocsVersion((v) => v + 1)} />
                </div>
                <div className="p-4 border rounded-lg bg-gray-50">
                  <UploadedDocuments orderId={order.order_id} currentUserId={user?.user_id} title="Shared Order Files" refreshKey={docsVersion} onChange={() => setDocsVersion((v) => v + 1)} />
                </div>
                {/* Proforma Invoice */}
                <div className="flex items-center justify-between p-4 border rounded-lg hover:bg-gray-50">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
                      <FileText className="w-5 h-5 text-red-600" />
                    </div>
                    <div>
                      <p className="font-medium">Proforma Invoice</p>
                      <p className="text-sm text-gray-500">Generated automatically</p>
                    </div>
                  </div>
                  <Button variant="outline" size="sm" onClick={handleDownloadInvoice}>
                    <Download className="w-4 h-4 mr-1" />
                    Download
                  </Button>
                </div>

                {/* Commercial Invoice */}
                <div className="flex items-center justify-between p-4 border rounded-lg hover:bg-gray-50">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                      <FileText className="w-5 h-5 text-blue-600" />
                    </div>
                    <div>
                      <p className="font-medium">Commercial Invoice</p>
                      <p className="text-sm text-gray-500">{order.status === 'confirmed' || order.status === 'completed' ? 'Available' : 'Pending order confirmation'}</p>
                    </div>
                  </div>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    disabled={order.status === 'pending'}
                    onClick={handleDownloadInvoice}
                  >
                    <Download className="w-4 h-4 mr-1" />
                    Download
                  </Button>
                </div>

                {/* Packing List */}
                <div className="flex items-center justify-between p-4 border rounded-lg hover:bg-gray-50">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                      <Package className="w-5 h-5 text-green-600" />
                    </div>
                    <div>
                      <p className="font-medium">Packing List</p>
                      <p className="text-sm text-gray-500">{['shipped', 'delivered', 'completed'].includes(order.status) ? 'Available' : 'Pending shipment'}</p>
                    </div>
                  </div>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    disabled={!['shipped', 'delivered', 'completed'].includes(order.status)}
                  >
                    <Download className="w-4 h-4 mr-1" />
                    Download
                  </Button>
                </div>

                {/* Certificate of Origin */}
                <div className="flex items-center justify-between p-4 border rounded-lg hover:bg-gray-50 bg-gradient-to-r from-continental/5 to-transparent">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-continental/20 rounded-lg flex items-center justify-center">
                      <CheckCircle className="w-5 h-5 text-continental" />
                    </div>
                    <div>
                      <p className="font-medium">AfCFTA Certificate of Origin</p>
                      <p className="text-sm text-gray-500">
                        {order.coo_status === 'approved' ? 'Approved' : 'Apply via Rules of Origin page'}
                      </p>
                    </div>
                  </div>
                  {order.coo_status === 'approved' ? (
                    <Button variant="outline" size="sm">
                      <Download className="w-4 h-4 mr-1" />
                      Download
                    </Button>
                  ) : (
                    <Button variant="outline" size="sm" asChild>
                      <Link to="/coo/apply">Apply Now</Link>
                    </Button>
                  )}
                </div>

                {/* Bill of Lading */}
                <div className="flex items-center justify-between p-4 border rounded-lg hover:bg-gray-50">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
                      <Truck className="w-5 h-5 text-orange-600" />
                    </div>
                    <div>
                      <p className="font-medium">Bill of Lading</p>
                      <p className="text-sm text-gray-500">{['shipped', 'delivered', 'completed'].includes(order.status) ? 'Available from logistics provider' : 'Pending shipment'}</p>
                    </div>
                  </div>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    disabled={!['shipped', 'delivered', 'completed'].includes(order.status)}
                  >
                    <Download className="w-4 h-4 mr-1" />
                    Download
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>

          {/* Payment Modal */}
          <Dialog open={showPayment} onOpenChange={setShowPayment}>
            <DialogContent className="max-w-md">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <CreditCard className="w-5 h-5 text-continental" />
                  Make Payment
                </DialogTitle>
                <DialogDescription>
                  Complete payment for order {order.order_id}
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-6 mt-4">
                {/* Order Summary */}
                <div className="p-4 bg-gray-50 rounded-lg space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Product</span>
                    <span className="font-medium">{order.product_name}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Quantity</span>
                    <span>{order.quantity} units</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Unit Price</span>
                    <span>{formatCurrency(order.unit_price, order.currency)}</span>
                  </div>
                  <hr className="my-2" />
                  <div className="flex justify-between font-semibold">
                    <span>Total Amount</span>
                    <span className="text-continental">{formatCurrency(order.quantity * order.unit_price, order.currency)}</span>
                  </div>
                </div>

                {/* Payment Methods */}
                <div className="space-y-3">
                  <p className="font-medium text-sm">Select Payment Method</p>
                  
                  <div className="p-4 border-2 border-continental rounded-lg bg-continental/5 cursor-pointer">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-continental rounded-lg flex items-center justify-center">
                        <span className="text-white font-bold text-xs">PAPSS</span>
                      </div>
                      <div className="flex-1">
                        <p className="font-medium">PAPSS (Pan-African Payment)</p>
                        <p className="text-xs text-gray-500">Instant cross-border payments</p>
                      </div>
                      <CheckCircle className="w-5 h-5 text-continental" />
                    </div>
                  </div>

                  <div className="p-4 border rounded-lg opacity-50 cursor-not-allowed">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-gray-200 rounded-lg flex items-center justify-center">
                        <CreditCard className="w-5 h-5 text-gray-500" />
                      </div>
                      <div>
                        <p className="font-medium text-gray-500">Card Payment</p>
                        <p className="text-xs text-gray-400">Coming soon</p>
                      </div>
                    </div>
                  </div>
                </div>

                <Button 
                  className="w-full bg-continental hover:bg-continental/90" 
                  onClick={handleSimulatePayment}
                  disabled={processingPayment}
                >
                  {processingPayment ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <CreditCard className="w-4 h-4 mr-2" />
                  )}
                  Pay {formatCurrency(order.quantity * order.unit_price, order.currency)}
                </Button>

                <p className="text-xs text-center text-gray-500">
                  Secured by PAPSS - Pan-African Payment & Settlement System
                </p>
              </div>
            </DialogContent>
          </Dialog>
        </main>
      </div>
    </div>
  );
};

// Timeline Item Component
const TimelineItem = ({ icon: Icon, title, date, active }) => (
  <div className="flex items-start gap-3">
    <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
      active ? 'bg-green-100 text-green-600' : 'bg-gray-100 text-gray-400'
    }`}>
      <Icon className="w-4 h-4" />
    </div>
    <div>
      <p className={`font-medium ${active ? 'text-charcoal' : 'text-gray-400'}`}>{title}</p>
      <p className="text-xs text-gray-500">{date}</p>
    </div>
  </div>
);

export default OrderDetailPage;
